import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, inject, Modal, ModalHandle, StatusCard } from 'Mri.Web.Common';
import * as React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledButtonDropdown } from 'reactstrap';
import { ActiveDisplay } from '../../ActiveDisplay';
import { DisplayEventsList } from '../../DisplayEventsList';
import { DisplayEventsModal } from '../../DisplayEventsModal';
import { DisplaysStore } from '../../DisplaysStore';
import { TicketModal } from '../../TicketModal';

export interface IDisplayEventsCardProps {
    readonly display: ActiveDisplay;
    readonly history: any;
}

@autoinject
@observer
export class DisplayEventsCard extends React.Component<IDisplayEventsCardProps> {
    public render() {
        return (
            <StatusCard title={this.renderTitle()}>
                <Input
                    type="text"
                    placeholder="Search Events..."
                    value={this.searchValue}
                    onChange={this.onSearchInputChange}
                    className="rounded-0 ticket-search"
                />

                <div className="scroll-container mt-3">
                    <div className="scroll-card">
                        {!!this.displayEvents?.length && (
                            <DisplayEventsList
                                displayEvents={this.displayEvents}
                                serialNumber={this.props.display.serialNumber}
                            />
                        )}
                    </div>
                </div>

                <DisplayEventsModal
                    display={this.props.display}
                    modalHandle={this.displayEventsModalHandle}
                    history={this.props.history}
                />

                {this.props.display && (
                    <TicketModal modalHandle={this.ticketModalHandle} display={this.props.display} />
                )}

                <Modal
                    className="modal-sm"
                    title="Confirm Action"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">This will ignore all current events. Are you sure?</div>
                </Modal>
            </StatusCard>
        );
    }

    @bind
    private renderTitle() {
        const noEvents = !this.displayEvents?.length;
        return (
            <div className="flex-items-center">
                {this.displayEvents?.length} {this.displayEvents?.length == 1 ? 'Event' : 'Events'}
                <UncontrolledButtonDropdown className="ms-auto">
                    <DropdownToggle caret color="link" className="fw-bold stat-link" disabled={noEvents}>
                        Actions
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onClick={() => this.displayEventsModalHandle.open()}>
                            Manage Incidents
                        </DropdownItem>
                        <DropdownItem onClick={this.openTicketModal}>Report Incidents</DropdownItem>
                    </DropdownMenu>
                </UncontrolledButtonDropdown>
            </div>
        );
    }

    @bind
    @action
    private onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @bind
    private async openTicketModal() {
        this.ticketModalHandle.open();
    }

    @bind
    private async clearAll() {
        if ((await this.modalHandle.confirm()) && this.displayEvents) {
            const grouped = this.displayEvents.find(de => de.count != undefined) != undefined;

            try {
                await this.displaysStore.displayEventBatchActionAsync(this.props.display.serialNumber, {
                    status: Mri.DisplayEvents.DisplayEventStatus.Ignored,
                    displayEventGuids: this.displayEvents.map(de => de.displayEventGuid),
                    grouped,
                });
            } catch (e: any) {
                console.error(e);
            }
        }
    }

    @computed
    private get displayEvents() {
        let displayEvents = this.displaysStore.currentEvents.get(this.props.display.serialNumber);
        if (this.searchValue && displayEvents)
            displayEvents = displayEvents.filter((o: any) => deepStringFind(o, this.searchValue));
        return displayEvents;
    }

    @observable
    private searchValue: string = '';
    private modalHandle = new ModalHandle();
    private ticketModalHandle = new ModalHandle();
    private displayEventsModalHandle = new ModalHandle();
    @inject
    private displaysStore!: DisplaysStore;
}
